
import { Table, Collection } from 'dexie';
import { BaseEntity, MutationOperation, MutationType } from '../types.js';
import { SonataDatabase, db, workspaceAccount } from './db.js';
import { SyncService } from './syncService.js';
import { getDeviceId } from './deviceId.js';

export interface ListParams<T> {
    filter?: (item: T) => boolean;
    sort?: string; 
    page?: number;
    pageSize?: number;
    where?: Record<string, any>; 
}

export class DexieRepository<T extends BaseEntity> {
    private db: SonataDatabase;
    private tableName: string;
    private table: Table<T>;
    private pkField: string;

    constructor(db: SonataDatabase, tableName: keyof SonataDatabase, pkField: string) {
        this.db = db;
        this.tableName = tableName as string;
        this.table = db[tableName] as unknown as Table<T>;
        this.pkField = pkField;
    }

    private triggerSync() {
        try {
            // Fire-and-forget: SyncService scheduler coalesces rapid mutation triggers
            SyncService.requestSync('mutation', { urgentMutationFlush: true });
        } catch (e) {
            console.warn('[Repository] Failed to trigger sync', e);
        }
    }

    private generateUUID(): string {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) {
            return crypto.randomUUID();
        }
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    async get(id: string): Promise<T | undefined> {
        const item = await this.table.get(id as any);
        if (item && item.deleted_at) return undefined;
        return item;
    }

    async list(params: ListParams<T> = {}): Promise<T[]> {
        let collection: Collection<T>;
        if (params.where) {
            collection = this.table.where(params.where);
        } else {
            collection = this.table.toCollection();
        }
        collection = collection.filter(item => {
            if (item.deleted_at) return false;
            if (params.filter) return params.filter(item);
            return true;
        });
        if (params.sort) {
            let results = await collection.sortBy(params.sort);
            if (params.page !== undefined && params.pageSize !== undefined) {
                const start = (params.page - 1) * params.pageSize;
                return results.slice(start, start + params.pageSize);
            }
            return results;
        }
        if (params.page !== undefined && params.pageSize !== undefined) {
            const offset = (params.page - 1) * params.pageSize;
            return await collection.offset(offset).limit(params.pageSize).toArray();
        }
        return await collection.toArray();
    }

    /**
     * Authoritative Upsert:
     * Enqueues mutation with the current local server_version as base_version.
     */
    async upsert(item: T): Promise<T> {
        if (this.db !== db || !workspaceAccount) throw new Error('Sign in to the correct workspace before saving.');
        const owner = workspaceAccount;
        const now = new Date().toISOString();
        const id = (item as any)[this.pkField];
        let isNew = false;
        
        // We need to know the base version for OCC
        const existing = id ? await this.table.get(id as any) : undefined;
        isNew = !existing;
        const baseVersion = existing ? (existing.server_version || 0) : 0;

        if (!id) {
            (item as any)[this.pkField] = this.generateUUID();
            item.created_at = now;
            item.updated_at = now;
            item.server_version = 0; // Local temporary version
            isNew = true;
        } else {
            item.updated_at = now;
            if (!item.created_at) item.created_at = now;
        }

        const finalId = (item as any)[this.pkField];
        const mutationId = this.generateUUID();

        const mutation: MutationOperation = {
            op_id: mutationId,
            mutation_id: mutationId,
            device_id: getDeviceId(),
            owner_account: owner,
            table_name: this.tableName,
            type: isNew ? 'create' : 'update',
            entity_id: finalId,
            base_version: baseVersion, // Authoritative field for the server to check
            payload: item,
            created_at_local: now,
            status: 'pending',
            retry_count: 0
        };

        if (this.db !== db || workspaceAccount !== owner) throw new Error('Workspace changed; save cancelled.');
        await (this.db as any).transaction('rw', [this.table, this.db.outbox], async () => {
            const latest = await this.table.get(finalId);
            mutation.base_version = latest?.server_version || 0;
            mutation.type = latest ? 'update' : 'create';
            item.server_version = mutation.base_version;
            await this.table.put(item);
            const last = await this.db.outbox.orderBy('created_at_local').last();
            mutation.created_at_local = new Date(Math.max(Date.now(), last ? Date.parse(last.created_at_local) + 1 : 0)).toISOString();
            await this.db.outbox.add(mutation);
        });

        this.triggerSync();
        return item;
    }

    async remove(id: string): Promise<void> {
        if (this.db !== db || !workspaceAccount) throw new Error('Sign in to the correct workspace before saving.');
        const owner = workspaceAccount;
        const now = new Date().toISOString();
        const item = await this.table.get(id as any);
        
        if (item) {
            const baseVersion = item.server_version || 0;
            item.deleted_at = now;
            item.updated_at = now;
            const mutationId = this.generateUUID();

            const mutation: MutationOperation = {
                op_id: mutationId,
                mutation_id: mutationId,
                device_id: getDeviceId(),
                owner_account: owner,
                table_name: this.tableName,
                type: 'delete',
                entity_id: id,
                base_version: baseVersion,
                payload: { [this.pkField]: id, deleted_at: now },
                created_at_local: now,
                status: 'pending',
                retry_count: 0
            };

            if (this.db !== db || workspaceAccount !== owner) throw new Error('Workspace changed; save cancelled.');
        await (this.db as any).transaction('rw', [this.table, this.db.outbox], async () => {
                const latest = await this.table.get(id);
                mutation.base_version = latest?.server_version || 0;
                item.server_version = mutation.base_version;
                await this.table.put(item);
                const last = await this.db.outbox.orderBy('created_at_local').last();
            mutation.created_at_local = new Date(Math.max(Date.now(), last ? Date.parse(last.created_at_local) + 1 : 0)).toISOString();
            await this.db.outbox.add(mutation);
            });

            this.triggerSync();
        }
    }

    async bulkUpsert(items: T[]): Promise<void> {
        for (const item of items) await this.upsert(item);
    }
}
